// OnboardingTestimonialsScreen — native (iOS + Android) full-screen
// version of the web <TestimonialsSection>. Sits between the
// competitors comparison and the quiz in the onboarding state
// machine (IOSOnboarding.jsx), matching the owner's request to show
// customer reviews right before Create-Your-Account.
//
// Same three reviews, styled as a vertical scroll list with a
// floating orange "Continue" pill that stays anchored to the bottom
// so users can move on at any time.

import { motion } from "framer-motion";
import { Star, Quote, ArrowRight, ArrowLeft } from "lucide-react";
import { TESTIMONIALS } from "@/lib/testimonialsData";

const COPY = {
  ar: {
    // Simplified per owner (Sep 30, 2026): the header is just a big
    // single title — no eyebrow badge, no subtitle.
    title: "آراء المستخدمين",
    continue: "متابعة",
  },
  en: {
    title: "User Reviews",
    continue: "Continue",
  },
};

const ACCENT_BG = {
  brand:   "bg-brand-500 text-white",
  emerald: "bg-emerald-600 text-white",
  violet:  "bg-violet-600 text-white",
  sky:     "bg-sky-600 text-white",
  amber:   "bg-amber-500 text-white",
};

export default function OnboardingTestimonialsScreen({ lang = "ar", onContinue }) {
  const isRTL = lang === "ar";
  const t = COPY[lang];

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="always-light fixed inset-0 z-[90] bg-neutral-50 flex flex-col"
      data-testid="onboarding-testimonials-screen"
    >
      {/* Header — single big title, no eyebrow / subtitle. */}
      <div
        className="relative px-5 pb-6 shadow-sm"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 24px)",
          background:
            "linear-gradient(180deg, #FFF7EE 0%, #FFEBD4 60%, #FFFFFF 100%)",
        }}
      >
        <h1
          className="text-4xl sm:text-5xl font-black text-neutral-900 leading-[1.05] tracking-tight text-center"
          data-testid="onboarding-testimonials-title"
        >
          {t.title}
        </h1>
      </div>

      {/* Scrollable testimonial cards. `pb-32` keeps the last one clear
          of the floating CTA. */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-32">
        <div className="max-w-md mx-auto space-y-4">
          {TESTIMONIALS.map((r, i) => (
            <TestimonialCard key={r.id} r={r} idx={i} lang={lang} isRTL={isRTL} />
          ))}
        </div>
      </div>

      {/* Floating Continue CTA — identical treatment to features +
          competitors screens for consistency across the onboarding. */}
      <div
        className="absolute inset-x-0 bottom-0 z-10 pointer-events-none"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 14px)",
          paddingTop: "24px",
          background:
            "linear-gradient(180deg, rgba(250,250,250,0) 0%, rgba(250,250,250,0.85) 40%, rgba(250,250,250,1) 100%)",
        }}
      >
        <div className="max-w-md mx-auto px-5 pointer-events-auto">
          <button
            type="button"
            onClick={onContinue}
            className="w-full orange-gradient text-white font-black py-4 rounded-full shadow-floating active:scale-[0.98] transition-transform text-base flex items-center justify-center gap-2"
            data-testid="onboarding-testimonials-continue"
          >
            {t.continue}
            {isRTL ? <ArrowLeft className="w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
}

function TestimonialCard({ r, idx, lang, isRTL }) {
  const name = lang === "en" ? r.name_en : r.name_ar;
  const role = lang === "en" ? r.role_en : r.role_ar;
  const text = lang === "en" ? r.text_en : r.text_ar;
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(idx * 0.08, 0.4) }}
      className="relative rounded-2xl bg-white p-5 border border-neutral-200/70"
      style={{
        boxShadow:
          "0 18px 38px -22px rgba(15,23,42,0.20), 0 4px 12px -6px rgba(249,115,22,0.15)",
      }}
      data-testid={`onb-testimonial-${r.id}`}
    >
      <Quote
        className={`absolute top-3 ${isRTL ? "left-3" : "right-3"} w-6 h-6 text-brand-200`}
        strokeWidth={2}
      />

      <div className="flex items-center gap-1 mb-3">
        {[0, 1, 2, 3, 4].slice(0, r.stars).map((i) => (
          <Star
            key={i}
            className="w-4.5 h-4.5 text-[#F5A623] fill-[#F5A623]"
            style={{
              width: "18px",
              height: "18px",
              animation: `onbTsTwinkle 2.4s ease-in-out ${i * 0.15}s infinite`,
            }}
          />
        ))}
      </div>

      <p className="text-[13.5px] leading-[1.8] text-neutral-700 whitespace-pre-line mb-4">
        {text}
      </p>

      <div className="flex items-center gap-3 pt-3 border-t border-neutral-100">
        <div
          className={`w-10 h-10 rounded-full ${ACCENT_BG[r.accent] || ACCENT_BG.brand} flex items-center justify-center font-black text-base shrink-0 shadow-md`}
        >
          {r.initials}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-neutral-900 text-[14px] leading-tight">
            {name}
          </div>
          {role && (
            <div className="text-[11.5px] text-neutral-500 mt-0.5">{role}</div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes onbTsTwinkle {
          0%, 100% { transform: scale(1);   filter: drop-shadow(0 0 0 rgba(245,166,35,0)); }
          45%      { transform: scale(1.2); filter: drop-shadow(0 0 5px rgba(245,166,35,0.85)); }
        }
      `}</style>
    </motion.article>
  );
}
